/**
 * Page-level Error Boundary
 * Wraps lazy-loaded route pages to prevent a single page crash
 * from taking down the entire application.
 */

import React, { ErrorInfo, ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';

interface PageErrorBoundaryProps {
  children: ReactNode;
  pageName?: string;
}

interface PageErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class PageErrorBoundary extends React.Component<PageErrorBoundaryProps, PageErrorBoundaryState> {
  constructor(props: PageErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): PageErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error(
      `[PageErrorBoundary] ${this.props.pageName ?? 'Unknown page'} crashed:`,
      error,
      errorInfo,
    );
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  private handleGoHome = (): void => {
    window.location.href = '/';
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const label = this.props.pageName ?? 'This page';

    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'var(--bg-app)',
          color: 'var(--text-primary)',
          padding: '40px 20px',
          textAlign: 'center',
        }}
      >
        <AlertTriangle size={48} color="var(--color-warning)" style={{ marginBottom: 16 }} />
        <h2 style={{ margin: '0 0 8px', fontSize: '1.25rem' }}>Something went wrong</h2>
        <p style={{ margin: '0 0 24px', fontSize: '0.875rem', opacity: 0.7, maxWidth: 420 }}>
          {label} encountered an unexpected error. You can retry loading it or return to the home
          page.
        </p>

        {this.state.error && (
          <pre
            style={{
              fontSize: '0.75rem',
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              padding: '10px 16px',
              maxWidth: 500,
              overflow: 'auto',
              marginBottom: 24,
              color: 'var(--text-secondary)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {this.state.error.message}
          </pre>
        )}

        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={this.handleRetry}
            style={{
              padding: '10px 20px',
              background: 'var(--bg-surface-alt)',
              color: 'var(--text-primary)',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
          >
            Retry
          </button>
          <button
            onClick={this.handleGoHome}
            style={{
              padding: '10px 20px',
              background: 'var(--accent)',
              color: '#ffffff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }
}

export default PageErrorBoundary;
